import { Component, ElementRef, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { WizardEngineService } from '../services/wizard-engine.service';
import { WizardStateService } from '../services/wizard-state.service';
import { WizardStep } from '../constants/wizard-steps.constants';

import { CurrentUserService } from '../../../services/current-user.service';
import { MapCreationService } from '../services/map-creation.service';
import { WorldBuilderAbortConfirmModalComponent } from './world-builder-abort-confirm-modal/world-builder-abort-confirm-modal.component';

@Component({
    selector: 'app-world-builder-modal',
    templateUrl: './world-builder-modal.component.html',
    styleUrls: ['./world-builder-modal.component.scss'],
    standalone: false
})
export class WorldBuilderModalComponent implements OnInit, OnDestroy {

  currentStep$: Observable<WizardStep>;
  @ViewChild('modalContainer') modalContainer!: ElementRef;
  private stepSub: Subscription | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private engineService: WizardEngineService,
    private stateService: WizardStateService,
    private currentUserService: CurrentUserService,
    private mapCreationService: MapCreationService,
    private cdr: ChangeDetectorRef
  ) {
    this.currentStep$ = this.stateService.state.pipe(
      map(state => state.currentStep)
    );
  }

  ngOnInit(): void {
    // Scroll modal container to top on step changes
    this.stepSub = this.currentStep$.subscribe(() => {
      setTimeout(() => {
        this.scrollToTop();
      }, 20);
    
          this.cdr.detectChanges();});

    // Reset state, but preserve mapId, mapName, mapIdPreset
    const currentState = this.stateService.currentState;
    this.stateService.resetState({
      mapId: currentState.mapId,
      mapName: currentState.mapName,
      mapIdPreset: currentState.mapIdPreset
    });

    // Fetch prefill data only when opening for an existing map (mapId pre-set)
    // For new map uploads, prefill is called from upload-map-step after upload completes
    if (!this.stateService.currentState.mapId) {
      return;
    }

    this.currentUserService.currentUser.subscribe(user => {
      if (user) {
        const userId = user.id || user.user_id;
        const mapId = this.stateService.currentState.mapId;
        if (mapId) {
          this.mapCreationService.getPrefillData(mapId, userId).subscribe({
            next: (data) => {
              this.stateService.updateState({
                mapName: data.map_name || this.stateService.currentState.mapName,
                cultures: data.cultures || [],
                religions: data.religions || [],
                states: data.states || [],
                provinces: data.provinces || [],
                wcreditBalance: data.wcredit_balance || 0,
                minYearsRequired: data.min_years_required || 1000,
                currentEra: data.current_era || null
              });
              this.cdr.detectChanges();
            },
            error: () => {
              this.activeModal.dismiss('Close click');
            
          this.cdr.detectChanges();}
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.stepSub) {
      this.stepSub.unsubscribe();
    }
  }

  scrollToTop(): void {
    if (this.modalContainer && this.modalContainer.nativeElement) {
      this.modalContainer.nativeElement.scrollTop = 0;
    }
  }

  close() {
    this.activeModal.dismiss('Close click');
  }

  onWorldCreated() {
    this.activeModal.close(true);
  }

  onCloseClick() {
    const currentState = this.stateService.currentState;
    const isBackground = !!currentState.isBackgroundProcessing ||
      currentState.creationStatus === 'processing_ai' ||
      currentState.creationStatus === 'processing_wiki';

    // When in background processing (processing_ai or processing_wiki),
    // no confirmation dialogue box needed — directly close the modal.
    if (isBackground) {
      this.onWorldCreated();
      return;
    }

    const modalRef = this.modalService.open(WorldBuilderAbortConfirmModalComponent, {
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.mapId = currentState.mapId;

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        const wasUploading = !currentState.mapIdPreset && !!currentState.isUploadInProgress && !!currentState.mapId;
        if (wasUploading) {
          this.activeModal.close(true);
        } else {
          this.close();
        }
      }
    
          this.cdr.detectChanges();}, () => {});
  }

  onNextStep() {
    this.engineService.nextStep();
  }

  onPreviousStep() {
    this.engineService.previousStep();
  }
}
